import React, { Fragment, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-apollo';

import MarcomLayout from '../../../components/global/MarcomLayout';
import SignupAlbumLayout from '../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import ErrorView from '../../../components/global/ErrorView';
import PasswordSetupStep from '../TeamMemberInvitationAcceptView/components/PasswordSetupStep';
import InvitationAcceptSuccessStep from '../TeamMemberInvitationAcceptView/components/InvitationAcceptSuccessStep';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { teamMember as teamMemberQuery } from '../../../graphql/queries/team_members';
import { teamMemberPasswordReset as teamMemberPasswordResetMutation } from '../../../graphql/mutations/team-members';
import { getErrorString } from '../../../util/normalize-errors';

const TeamMemberPasswordResetView = () => {
  const location = useLocation();
  const openAlert = useOpenAlert();
  const [passwordReset, setPasswordReset] = useState(false);
  const { teamMemberId, token } = qs.parse(location.search ?? '');

  const { data: { teamMember } = {}, loading: queryLoading } = useQuery(
    teamMemberQuery,
    {
      variables: { team_member_id: teamMemberId, token },
      skip: !teamMemberId || !token,
    },
  );

  const [teamMemberPasswordReset, { loading: mutationLoading }] = useMutation(
    teamMemberPasswordResetMutation,
  );

  const onSubmit = async values => {
    if (!values.password || values.password.length < 8) {
      openAlert({
        message: 'Password must be at least 8 characters long',
      });
      return;
    }
    if (values.password !== values.passwordConfirmation) {
      openAlert({
        message: 'Passwords do not match',
      });
      return;
    }
    try {
      await teamMemberPasswordReset({
        variables: {
          input: {
            team_member_id: teamMemberId,
            token,
            password: values.password,
          },
        },
      });
      setPasswordReset(true);
    } catch (e) {
      openAlert({
        message: 'There was an error resetting the password',
        info: getErrorString(e),
      });
    }
  };

  if (queryLoading) {
    return <FullScreenLoader />;
  }

  if (!teamMember || !teamMember.has_password) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'It looks like the password reset link provided does not match any of our records.',
          },
        }}
      />
    );
  }

  const teamUsername = teamMember.org_account?.username;
  const loginPath = teamUsername
    ? `/shutterbug/signin?defaultUserMode=TEAM&team=${teamUsername}`
    : '/shutterbug/signin?defaultUserMode=TEAM';

  if (passwordReset) {
    return (
      <Fragment>
        <Helmet>
          <title>Waldo &ndash; Password Reset</title>
        </Helmet>
        <div className="team-member-invitation-success-wrapper">
          <SignupAlbumLayout.Wrapper
            imageDisabled
            logoSrc={null}
            withCTA={false}
            className="team-member-invitation-success-layout"
          >
            <InvitationAcceptSuccessStep
              loginPath={loginPath}
              message={
                <>
                  Your password has been{' '}
                  <span className="text-primary text-bold">updated!</span>
                </>
              }
            />
          </SignupAlbumLayout.Wrapper>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Reset Team Password</title>
      </Helmet>
      <MarcomLayout.Wrapper
        title={
          <>
            Reset your password for{' '}
            <span className="text-primary">{teamUsername}</span>
          </>
        }
        signupDisabled
      >
        <Formik
          onSubmit={onSubmit}
          initialValues={{ password: '', passwordConfirmation: '' }}
          render={({ values, handleSubmit, handleChange }) => (
            <PasswordSetupStep
              loading={mutationLoading}
              handleSubmit={handleSubmit}
              handleChange={handleChange}
              values={values}
              helperText="Enter a new password to regain access to your team account."
              submitLabel="Reset password"
            />
          )}
        />
      </MarcomLayout.Wrapper>
    </Fragment>
  );
};

export default TeamMemberPasswordResetView;
